import { useQuery } from "@apollo/client";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import CloseButton from "react-bootstrap/CloseButton";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import apiClient from "../api";
import { gettext } from "../i18n";
import SessionTimeout from "components/common/SessionTimeout";
import { SearchableOrganizationPicker } from "components/common/SearchableOrganizationPicker";
import {
  SearchableStationPicker,
  SearchableStation,
} from "components/common/SearchableStationPicker";
import DefaultTooltip from "components/common/DefaultTooltip";
import { GET_ORGANIZATION_MAPS } from "utils/users";
import "./OrganizationMapsPage.scss";

interface MapStation {
  id: number;
  station_id: number;
  station: {
    id: number;
    identifier: string;
    name: string;
    lat?: number | null;
    lon?: number | null;
  };
}

interface OrganizationMap {
  id: number;
  name: string;
  active: boolean;
  organization_id: number;
  organization: {
    id: number;
    name: string;
    slug: string;
  };
  organization_map_stations: MapStation[];
}

interface GetOrganizationMapsData {
  organization_map: OrganizationMap[];
}

interface MapRowProps {
  map: OrganizationMap;
  onSuccess: () => void;
  onEdit: (map: OrganizationMap) => void;
  onManageStations: (map: OrganizationMap) => void;
}

const MapRow = ({ map, onSuccess, onEdit, onManageStations }: MapRowProps) => {
  const handleToggle = (activate: boolean) => {
    apiClient
      .request("/organization-admin/map/toggle", {
        method: "POST",
        data: { id: map.id, active: activate },
      })
      .then(() => {
        const message = activate
          ? gettext("Organization map activated successfully")
          : gettext("Organization map deactivated successfully");
        toast.success(message);
        onSuccess();
      })
      .catch((e) => {
        console.error(e);
        const errorMessage = activate
          ? gettext("Error activating organization map")
          : gettext("Error deactivating organization map");
        toast.error(
          `${errorMessage}: ${e.message?.toString() || "Unknown error"}`,
        );
      });
  };

  const isActive = map.active;

  return (
    <tr key={map.id}>
      <td data-label={gettext("Organization")}>{map.organization.name}</td>
      <td data-label={gettext("Name")}>{map.name}</td>
      <td data-label={gettext("Actions")}>
        <div className="d-flex flex-wrap gap-1">
          {isActive ? (
            <>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => handleToggle(false)}
              >
                {gettext("Deactivate")}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => onEdit(map)}
              >
                {gettext("Rename")}
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => onManageStations(map)}
              >
                {gettext("Manage stations")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleToggle(true)}
              >
                {gettext("Activate")}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => onEdit(map)}
              >
                {gettext("Rename")}
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => onManageStations(map)}
              >
                {gettext("Manage stations")}
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

interface MapsTableProps {
  maps: OrganizationMap[];
  onSuccess: () => void;
  onEdit: (map: OrganizationMap) => void;
  onManageStations: (map: OrganizationMap) => void;
}

const MapsTable = ({
  maps,
  onSuccess,
  onEdit,
  onManageStations,
}: MapsTableProps) => {
  const activeMaps = maps.filter((m) => m.active);
  const inactiveMaps = maps.filter((m) => !m.active);

  return (
    <div className="organization-maps-section">
      <h5 className="mb-3">{gettext("Active Maps")}</h5>
      <div className="overflow-auto">
        <Table style={{ tableLayout: "auto" }} className="mb-4">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>{gettext("Organization")}</th>
              <th style={{ width: "40%" }}>{gettext("Name")}</th>
              <th style={{ width: "auto" }}>{gettext("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {activeMaps.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center">
                  {gettext("No active organization maps")}
                </td>
              </tr>
            ) : (
              activeMaps.map((map) => (
                <MapRow
                  key={map.id}
                  map={map}
                  onSuccess={onSuccess}
                  onEdit={onEdit}
                  onManageStations={onManageStations}
                />
              ))
            )}
          </tbody>
        </Table>
      </div>

      {inactiveMaps.length > 0 && (
        <div className="organization-maps-inactive">
          <h5 className="mb-3">{gettext("Inactive Maps")}</h5>
          <div className="overflow-auto">
            <Table style={{ tableLayout: "auto" }}>
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>{gettext("Organization")}</th>
                  <th style={{ width: "40%" }}>{gettext("Name")}</th>
                  <th style={{ width: "auto" }}>{gettext("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {inactiveMaps.map((map) => (
                  <MapRow
                    key={map.id}
                    map={map}
                    onSuccess={onSuccess}
                    onEdit={onEdit}
                    onManageStations={onManageStations}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

interface AddMapModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const AddMapModal = ({ show, onHide, onSuccess }: AddMapModalProps) => {
  const [selectedOrgId, setSelectedOrgId] = React.useState<string>("");
  const [mapName, setMapName] = React.useState<string>("");

  const handleClose = () => {
    setSelectedOrgId("");
    setMapName("");
    onHide();
  };

  const handleConfirm = () => {
    if (!selectedOrgId || !mapName.trim()) {
      return;
    }
    apiClient
      .request("/organization-admin/map/create", {
        method: "POST",
        data: {
          organization_id: parseInt(selectedOrgId, 10),
          name: mapName.trim(),
        },
      })
      .then(() => {
        toast.success(gettext("Organization map created successfully"));
        handleClose();
        onSuccess();
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `${gettext("Error creating organization map")}: ${e.message?.toString() || "Unknown error"}`,
        );
      });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{gettext("Add new organization map")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Organization")}</Form.Label>
          <SearchableOrganizationPicker
            value={selectedOrgId}
            onChange={setSelectedOrgId}
            placeholder={gettext("Select organization...")}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Map name")}</Form.Label>
          <Form.Control
            type="text"
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            placeholder={gettext("Enter map name")}
            maxLength={32}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          {gettext("Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedOrgId || !mapName.trim()}
        >
          {gettext("Create")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface EditMapModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  map: OrganizationMap | null;
}

const EditMapModal = ({ show, onHide, onSuccess, map }: EditMapModalProps) => {
  const [mapName, setMapName] = React.useState<string>("");

  React.useEffect(() => {
    if (map) {
      setMapName(map.name);
    }
  }, [map]);

  const handleClose = () => {
    setMapName("");
    onHide();
  };

  const handleConfirm = () => {
    if (!map || !mapName.trim()) {
      return;
    }
    apiClient
      .request("/organization-admin/map/update", {
        method: "POST",
        data: {
          id: map.id,
          name: mapName.trim(),
        },
      })
      .then(() => {
        toast.success(gettext("Organization map updated successfully"));
        handleClose();
        onSuccess();
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `${gettext("Error updating organization map")}: ${e.message?.toString() || "Unknown error"}`,
        );
      });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{gettext("Rename organization map")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Organization")}</Form.Label>
          <Form.Control
            type="text"
            value={map?.organization.name ?? ""}
            disabled
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Map name")}</Form.Label>
          <Form.Control
            type="text"
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            placeholder={gettext("Enter map name")}
            maxLength={32}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          {gettext("Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!mapName.trim()}
        >
          {gettext("Save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface StationLozengeProps {
  station: SearchableStation;
  onRemove: () => void;
}

const StationLozenge = ({ station, onRemove }: StationLozengeProps) => {
  const hasNoCoordinates =
    station.lat === null ||
    station.lat === undefined ||
    station.lon === null ||
    station.lon === undefined;

  return (
    <Badge
      bg={hasNoCoordinates ? "warning" : "secondary"}
      className="d-inline-flex align-items-center gap-1 px-2 py-1"
      style={{ fontSize: "0.875rem" }}
    >
      {hasNoCoordinates && (
        <DefaultTooltip
          popHeader={gettext("No coordinates set")}
          popBody={
            <>
              {gettext(
                "This station does not have latitude and longitude set, which means it will not appear on the map.",
              )}
            </>
          }
          iconClassName="me-1"
        />
      )}
      <span className="text-truncate" style={{ maxWidth: "200px" }}>
        {station.identifier}
      </span>
      <CloseButton
        onClick={onRemove}
        variant="white"
        style={{ fontSize: "0.5rem", padding: "0.25rem" }}
      />
    </Badge>
  );
};

interface ManageStationsModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  map: OrganizationMap | null;
}

const ManageStationsModal = ({
  show,
  onHide,
  onSuccess,
  map,
}: ManageStationsModalProps) => {
  const [selectedStationId, setSelectedStationId] = React.useState<string>("");
  const [stations, setStations] = React.useState<SearchableStation[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showAllOrganizations, setShowAllOrganizations] = React.useState(false);
  const [includeStationsWithoutOrg, setIncludeStationsWithoutOrg] =
    React.useState(true);

  React.useEffect(() => {
    if (map) {
      const existingStations = map.organization_map_stations.map((ms) => ({
        id: ms.station.id,
        identifier: ms.station.identifier,
        name: ms.station.name,
        lat: ms.station.lat,
        lon: ms.station.lon,
      }));
      setStations(existingStations);
    }
  }, [map]);

  const handleClose = () => {
    setSelectedStationId("");
    setStations([]);
    setShowAllOrganizations(false);
    setIncludeStationsWithoutOrg(true);
    onHide();
  };

  const handleAddStation = (station: SearchableStation | null) => {
    if (!station) {
      return;
    }
    const alreadyExists = stations.some((s) => s.id === station.id);
    if (alreadyExists) {
      toast.info(gettext("Station already added"));
      return;
    }
    setStations((prev) => [...prev, station]);
    setSelectedStationId("");
  };

  const handleRemoveStation = (stationId: number) => {
    setStations((prev) => prev.filter((s) => s.id !== stationId));
  };

  const handleSave = () => {
    if (!map) {
      return;
    }
    setIsSaving(true);
    apiClient
      .request("/organization-admin/map/update", {
        method: "POST",
        data: {
          id: map.id,
          name: map.name,
          station_ids: stations.map((s) => s.id),
        },
      })
      .then(() => {
        toast.success(gettext("Map stations updated successfully"));
        handleClose();
        onSuccess();
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `${gettext("Error updating map stations")}: ${e.message?.toString() || "Unknown error"}`,
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const stationFilter = React.useCallback(
    (station: SearchableStation) => !stations.some((s) => s.id === station.id),
    [stations],
  );

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{gettext("Manage map stations")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Organization")}</Form.Label>
          <Form.Control
            type="text"
            value={map?.organization.name ?? ""}
            disabled
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{gettext("Map")}</Form.Label>
          <Form.Control type="text" value={map?.name ?? ""} disabled />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>{gettext("Add station")}</Form.Label>
          <SearchableStationPicker
            value={selectedStationId}
            onChange={setSelectedStationId}
            onStationSelect={handleAddStation}
            filter={stationFilter}
            placeholder={gettext("Select station to add...")}
            organizationId={
              showAllOrganizations ? undefined : map?.organization_id
            }
            includeWithoutOrganization={includeStationsWithoutOrg}
            checkCoordinates
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Check
            type="checkbox"
            id="show-all-organizations"
            label={gettext("Show stations from all organizations")}
            checked={showAllOrganizations}
            onChange={(e) => setShowAllOrganizations(e.target.checked)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="include-stations-without-org"
            label={gettext("Include stations without an organization")}
            checked={includeStationsWithoutOrg}
            onChange={(e) => setIncludeStationsWithoutOrg(e.target.checked)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>{gettext("Selected stations")}</Form.Label>
          {stations.length === 0 ? (
            <div className="text-muted">{gettext("No stations selected")}</div>
          ) : (
            <div className="map-stations-list">
              {stations.map((station) => (
                <StationLozenge
                  key={station.id}
                  station={station}
                  onRemove={() => handleRemoveStation(station.id)}
                />
              ))}
            </div>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          {gettext("Cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? gettext("Saving...") : gettext("Save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const OrganizationMapsPage = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState("");
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [editingMap, setEditingMap] = React.useState<OrganizationMap | null>(
    null,
  );
  const [managingStationsMap, setManagingStationsMap] =
    React.useState<OrganizationMap | null>(null);

  const debouncedSetQuery = React.useMemo(
    () => debounce((value: string) => setDebouncedSearchQuery(value), 400),
    [],
  );

  React.useEffect(() => {
    debouncedSetQuery(searchQuery);
    return () => debouncedSetQuery.cancel();
  }, [searchQuery, debouncedSetQuery]);

  const { data, error, refetch } = useQuery<GetOrganizationMapsData>(
    GET_ORGANIZATION_MAPS,
  );

  const filteredMaps = React.useMemo(() => {
    const maps = data?.organization_map ?? [];
    if (!debouncedSearchQuery.trim()) {
      return maps;
    }
    const query = debouncedSearchQuery.toLowerCase();
    return maps.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.organization.name.toLowerCase().includes(query),
    );
  }, [data?.organization_map, debouncedSearchQuery]);

  const handleEdit = React.useCallback((map: OrganizationMap) => {
    setEditingMap(map);
  }, []);

  const handleCloseEdit = React.useCallback(() => {
    setEditingMap(null);
  }, []);

  const handleManageStations = React.useCallback((map: OrganizationMap) => {
    setManagingStationsMap(map);
  }, []);

  const handleCloseManageStations = React.useCallback(() => {
    setManagingStationsMap(null);
  }, []);

  if (error) {
    console.error("OrganizationMapsPage GQL error: ", error);
    return <SessionTimeout />;
  }

  return (
    <>
      <PageHeader
        title={gettext("Organization Maps")}
        description={gettext(
          "Manage organization maps for displaying stations",
        )}
      >
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowAddModal(true)}
        >
          {gettext("Add new map")}
        </Button>
      </PageHeader>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col xs={12} lg={6}>
              <Form.Label className="fw-semibold">
                {gettext("Search")}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Search by organization or map name")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
          </Row>
          <MapsTable
            maps={filteredMaps}
            onSuccess={() => refetch()}
            onEdit={handleEdit}
            onManageStations={handleManageStations}
          />
        </Card.Body>
      </Card>
      <AddMapModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={() => refetch()}
      />
      <EditMapModal
        show={editingMap !== null}
        onHide={handleCloseEdit}
        onSuccess={() => refetch()}
        map={editingMap}
      />
      <ManageStationsModal
        show={managingStationsMap !== null}
        onHide={handleCloseManageStations}
        onSuccess={() => refetch()}
        map={managingStationsMap}
      />
    </>
  );
};

export default OrganizationMapsPage;
